import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TagOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import axiosInstance from "../../variables/api/axiosInstance";
import { API_URL, LOGGED_USER, TOKEN } from "../../variables/constants";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import Footer from "../../components/Footer";
import ActionDropdown from "../production/components/ActionDropdown";

const { Title, Text } = Typography;

const CAT_COLORS = { MOTO: "blue", AUTO: "orange", CAMION: "purple", BUS: "cyan" };
const getCatColor = (nom) => CAT_COLORS[nom?.toUpperCase()] || "default";

// Droit d'ÉDITION des garanties : le Pool modifie tout ; une compagnie ne modifie que SES
// propres garanties ; les garanties générales (sans organisme, ou organisme du Pool) sont en
// lecture seule pour les non-Pool. La visibilité (lecture) est déjà filtrée côté backend.
const _isPoolUser = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
const _userOrgCode = LOGGED_USER?.profil?.organisme?.code;
const canEditGarantie = (record) => {
  if (_isPoolUser) return true;
  const org = record?.organisme;
  if (!org || org.typeOrganisme === "POOL") return false; // garantie générale
  return !!_userOrgCode && org.code === _userOrgCode;
};

const GarantiesV2 = () => {
  const [garanties, setGaranties] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [activeCategory, setActiveCategory] = useState("TOUS");
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedGarantie, setSelectedGarantie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchGaranties = () => {
    axios
      .get(`${API_URL}/api/garantie`, {
        headers: { Authorization: `Bearer ${TOKEN}`, "Content-Type": "application/json" },
      })
      .then((res) => setGaranties(res.data))
      .catch(() => toast.error("Erreur lors de la récupération des garanties"));
  };

  useEffect(() => {
    fetchGaranties();
    axiosInstance
      .get("/api/categorie")
      .then((res) => setCategories(res.data))
      .catch(() => toast.error("Erreur lors de la récupération des catégories"));
  }, []);

  const openAdd = () => {
    setEditMode(false);
    setSelectedGarantie(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditMode(true);
    setSelectedGarantie(record);
    form.setFieldsValue({
      nom: record.nom,
      commentaire: record.commentaire,
      obligatoire: record.obligatoire || false,
      // null/undefined/true = fractionnable (défaut) ; false = non fractionnable
      fractionnable: record.fractionnable !== false,
      // Éligibilité aux réductions paramétrables (flotte / bonus-malus)
      flotte: record.flotte || false,
      bonus: record.bonus || false,
      categorieId: record.categorie?.categorieId,
    });
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    form.resetFields();
  };

  const handleSubmit = (values) => {
    setSubmitting(true);
    const selectedCat = categories.find((c) => c.categorieId === values.categorieId);
    const payload = {
      nom: values.nom,
      commentaire: values.commentaire || "",
      obligatoire: values.obligatoire || false,
      // false = prime non fractionnable (plein montant partout) ; sinon fractionnable
      fractionnable: values.fractionnable !== false,
      // Réductions paramétrables : la garantie est-elle réduite par la flotte / le bonus-malus ?
      flotte: values.flotte || false,
      bonus: values.bonus || false,
      categorie: selectedCat,
    };
    if (editMode && selectedGarantie?.codeId) {
      payload.codeId = selectedGarantie.codeId;
    }
    const req = editMode
      ? axios.put(`${API_URL}/api/garantie`, JSON.stringify(payload), {
          headers: { Authorization: `Bearer ${TOKEN}`, "Content-Type": "application/json" },
        })
      : axios.post(`${API_URL}/api/garantie`, JSON.stringify(payload), {
          headers: { Authorization: `Bearer ${TOKEN}`, "Content-Type": "application/json" },
        });

    req
      .then(() => {
        toast.success(editMode ? "Garantie modifiée avec succès" : "Garantie ajoutée avec succès");
        handleModalClose();
        fetchGaranties();
      })
      .catch(() => toast.error(editMode ? "Erreur lors de la modification" : "Erreur lors de l'ajout"))
      .finally(() => setSubmitting(false));
  };

  const confirmDelete = (codeId) => {
    Modal.confirm({
      title: "Supprimer cette garantie ?",
      icon: <ExclamationCircleFilled style={{ color: "#ff4d4f" }} />,
      content: "Cette action est irréversible. Toutes les sous-garanties associées seront également affectées.",
      okText: "Supprimer",
      okButtonProps: { danger: true },
      cancelText: "Annuler",
      onOk() {
        axios
          .delete(`${API_URL}/api/garantie/${codeId}`, {
            headers: { Authorization: `Bearer ${TOKEN}` },
          })
          .then(() => {
            toast.success("Garantie supprimée");
            fetchGaranties();
          })
          .catch(() => toast.error("Erreur lors de la suppression"));
      },
    });
  };

  const catNames = [...new Set(garanties.map((g) => g.categorie?.nom || "Non catégorisé"))];

  const filteredGaranties = garanties.filter((g) => {
    const matchSearch = !searchText || g.nom?.toLowerCase().includes(searchText.toLowerCase());
    const matchCat =
      activeCategory === "TOUS" || (g.categorie?.nom || "Non catégorisé") === activeCategory;
    return matchSearch && matchCat;
  });

  const tabItems = [
    { key: "TOUS", label: `Toutes (${garanties.length})` },
    ...catNames.map((cn) => ({
      key: cn,
      label: `${cn} (${garanties.filter((g) => (g.categorie?.nom || "Non catégorisé") === cn).length})`,
    })),
  ];

  const columns = [
    {
      title: "Garantie",
      dataIndex: "nom",
      key: "nom",
      render: (nom, record) => (
        <Space>
          <SafetyCertificateOutlined style={{ color: "#667eea" }} />
          <Text strong>{nom}</Text>
          {record.obligatoire && (
            <Tag color="red" style={{ fontSize: 10, padding: "0 5px", borderRadius: 20 }}>
              Obligatoire
            </Tag>
          )}
          {record.fractionnable === false && (
            <Tag color="gold" style={{ fontSize: 10, padding: "0 5px", borderRadius: 20 }}>
              Non fractionnable
            </Tag>
          )}
          {record.flotte && (
            <Tag color="geekblue" style={{ fontSize: 10, padding: "0 5px", borderRadius: 20 }}>
              Flotte
            </Tag>
          )}
          {record.bonus && (
            <Tag color="cyan" style={{ fontSize: 10, padding: "0 5px", borderRadius: 20 }}>
              BNS
            </Tag>
          )}
          {!canEditGarantie(record) && (
            <Tag style={{ fontSize: 10, padding: "0 5px", borderRadius: 20 }}>
              Lecture seule
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: "Catégorie",
      dataIndex: "categorie",
      key: "categorie",
      render: (cat) => (
        <Tag color={getCatColor(cat?.nom)} style={{ borderRadius: 20 }}>
          {cat?.nom || "—"}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "commentaire",
      key: "commentaire",
      ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const editable = canEditGarantie(record);
        const actions = [
          {
            key: "sousGaranties",
            label: "Sous-garanties",
            icon: <EyeOutlined />,
            onClick: () => navigate(`/sous-garanties/${record.codeId}`),
          },
        ];
        if (editable) {
          actions.unshift({
            key: "edit",
            label: "Modifier",
            icon: <EditOutlined />,
            onClick: () => openEdit(record),
          });
          actions.push({
            key: "delete",
            label: "Supprimer",
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => confirmDelete(record.codeId),
          });
        }
        return <ActionDropdown actions={actions} />;
      },
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"TARIFICATION"} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* Header */}
              <div
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: 12,
                  padding: "24px 28px",
                  marginBottom: 24,
                  color: "#fff",
                }}
              >
                <Row justify="space-between" align="middle">
                  <Col>
                    <Space align="center">
                      <SafetyCertificateOutlined style={{ fontSize: 36 }} />
                      <div>
                        <Title level={3} style={{ color: "#fff", margin: 0 }}>
                          Gestion des Garanties
                        </Title>
                        <Text style={{ color: "rgba(255,255,255,0.8)" }}>
                          Paramétrage des garanties par catégorie d'engin
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  <Col>
                    <Button
                      icon={<PlusOutlined />}
                      size="large"
                      onClick={openAdd}
                      style={{
                        background: "#fff",
                        color: "#667eea",
                        border: "none",
                        fontWeight: 600,
                        borderRadius: 8,
                      }}
                    >
                      Nouvelle garantie
                    </Button>
                  </Col>
                </Row>
              </div>

              {/* Stats */}
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8} md={6}>
                  <Card bordered={false} style={{ borderRadius: 10, textAlign: "center" }}>
                    <Statistic
                      title="Total garanties"
                      value={garanties.length}
                      prefix={<SafetyCertificateOutlined />}
                      valueStyle={{ color: "#667eea" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Card bordered={false} style={{ borderRadius: 10, textAlign: "center" }}>
                    <Statistic
                      title="Obligatoires"
                      value={garanties.filter((g) => g.obligatoire).length}
                      prefix={<CheckOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Card bordered={false} style={{ borderRadius: 10, textAlign: "center" }}>
                    <Statistic
                      title="Catégories"
                      value={catNames.length}
                      prefix={<TagOutlined />}
                      valueStyle={{ color: "#fa8c16" }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Table card */}
              <Card bordered={false} style={{ borderRadius: 12 }} styles={{ body: { padding: 0 } }}>
                <div style={{ padding: "16px 24px 0" }}>
                  <Row justify="space-between" align="middle" wrap={false}>
                    <Col flex="auto">
                      <Tabs
                        items={tabItems}
                        activeKey={activeCategory}
                        onChange={setActiveCategory}
                        size="small"
                      />
                    </Col>
                    <Col>
                      <Input
                        prefix={<SearchOutlined />}
                        placeholder="Rechercher une garantie..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 240, borderRadius: 8 }}
                        allowClear
                      />
                    </Col>
                  </Row>
                </div>
                <Divider style={{ margin: "0 0 4px" }} />
                <Table
                  columns={columns}
                  dataSource={filteredGaranties}
                  rowKey="codeId"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `${total} garantie(s)`,
                  }}
                />
              </Card>
            </div>
            <Footer />
            <div className="content-backdrop fade"></div>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal
        open={modalOpen}
        onCancel={handleModalClose}
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: "#667eea" }} />
            <span>{editMode ? "Modifier la garantie" : "Nouvelle garantie"}</span>
          </Space>
        }
        footer={null}
        width={560}
        destroyOnClose
      >
        <Divider style={{ margin: "8px 0 20px" }} />
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ fractionnable: true }}
        >
          <Form.Item
            name="nom"
            label="Nom de la garantie"
            rules={[{ required: true, message: "Le nom est requis" }]}
          >
            <Input placeholder="Ex: Responsabilité Civile" />
          </Form.Item>

          <Form.Item
            name="categorieId"
            label="Catégorie"
            rules={[{ required: true, message: "La catégorie est requise" }]}
          >
            <Select placeholder="Sélectionner une catégorie" optionLabelProp="label">
              {categories.map((cat) => (
                <Select.Option key={cat.categorieId} value={cat.categorieId} label={cat.nom}>
                  <Tag color={getCatColor(cat.nom)} style={{ marginRight: 6 }}>
                    {cat.nom}
                  </Tag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="commentaire" label="Description">
            <Input.TextArea rows={3} placeholder="Description optionnelle de la garantie..." />
          </Form.Item>

          {/* Seul le Pool peut rendre une garantie obligatoire ; pour une compagnie elle
              reste toujours optionnelle. */}
          {_isPoolUser && (
            <Form.Item name="obligatoire" label="Garantie obligatoire" valuePropName="checked">
              <Switch checkedChildren="OUI" unCheckedChildren="NON" />
            </Form.Item>
          )}

          <Form.Item
            name="fractionnable"
            label="Prime fractionnable"
            valuePropName="checked"
            extra="Désactivez pour une prime à plein montant à chaque échéance (jamais réduite par le fractionnement, à l'affaire nouvelle, au renouvellement et à la prorogation)."
          >
            <Switch checkedChildren="OUI" unCheckedChildren="NON" />
          </Form.Item>

          <Divider orientation="left" plain style={{ fontSize: 12 }}>
            Réductions applicables
          </Divider>

          <Form.Item
            name="flotte"
            label="Réductible par la flotte"
            valuePropName="checked"
            extra="Si activé, la prime de cette garantie est diminuée par la réduction flotte (contrats de plus d'un engin)."
          >
            <Switch checkedChildren="OUI" unCheckedChildren="NON" />
          </Form.Item>

          <Form.Item
            name="bonus"
            label="Réductible par le bonus/malus"
            valuePropName="checked"
            extra="Si activé, la prime de cette garantie suit le coefficient bonus/malus (bonus = réduction, malus = majoration)."
          >
            <Switch checkedChildren="OUI" unCheckedChildren="NON" />
          </Form.Item>

          <Divider />
          <Row justify="end" gutter={8}>
            <Col>
              <Button onClick={handleModalClose}>Annuler</Button>
            </Col>
            <Col>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                style={{
                  background: editMode
                    ? "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  borderRadius: 8,
                }}
              >
                {editMode ? "Enregistrer les modifications" : "Créer la garantie"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default GarantiesV2;
